import React, { useState, Fragment, useEffect } from 'react';
import styles from './styles.scss';
import classnames from 'classnames';
import { useParams } from 'react-router-dom';
import ListViewTwoRowHeader from '../../ListViewTwoRowHeader';

import useRxStore from '../../../utils/use-rx-store';
import useRxDispatch from '../../../utils/use-rx-dispatch';

import { Hosts } from '../../../utils/host';
import { ON_PREM } from "../../../utils/environment";

import { ResourceStatus } from '../../../types/resource';
import { SpacesActionTypes } from '../../../types/spaces';
import SpacesStore from '../../../rx-stores/spaces';

import {
  AppBar,
  AppBarSection,
  AppBarTitle,
  Button,
  Icons,
  ListView,
  ListViewColumn,
  ListViewColumnSpacer,
  Skeleton,
} from '@densityco/ui';
import colorVariables from '@densityco/ui/variables/colors.json';
import DataGrid, { FORCE_BREAK } from '../../DataGrid';
import ColoredPercent from '../../ColoredPercent';
import HorizontalForm from '../../HorizontalForm';
import CountsChart from '../CountsChart/index';
import PeoplePluralizer from '../../PeoplePluralizer';
import Loading from '../../Loading';
import ErrorState from '../../ErrorState';
import CaptureScheduleModal from '../../CaptureScheduleModal';
import BackButton from '../../BackButton';
import SensorActionButtons from '../../Sensors/ActionButtons';
import DetailsButton from '../../DetailsButton';
import { openInNewTab } from '../../../utils/navigation';

const SENSOR_STATUS_COLOR = {
  online: colorVariables.green,
  error: colorVariables.red,
};

const SpaceDetail: React.FunctionComponent<{}> = () => {
  const state = useRxStore(SpacesStore);
  const dispatch = useRxDispatch();
  const { organizationIdOrAll, spaceId } = useParams();

  const [captureScheduleModalOpen, setCaptureScheduleModalOpen] = useState(
    false,
  );

  useEffect(() => {
    dispatch({
      type: SpacesActionTypes.SPACES_CHANGE_PAGE_DETAIL,
      id: spaceId,
    });
  }, [spaceId]);

  useEffect(() => {
    if (state.resource.status !== ResourceStatus.COMPLETE) {
      return;
    }
    document.title = `Space ${state.resource.data.space.name} - Density Sensei`;
    return () => {
      document.title = 'Density Sensei';
    };
  }, [
    state.resource.status === ResourceStatus.COMPLETE
      ? state.resource.data.space
      : null,
  ]);

  if (state.resource.status === ResourceStatus.ERROR) {
    return (
        <div className={styles.wrapper}>
          <AppBar>
            <AppBarTitle>
              <BackButton />
              <Skeleton width={200} height={24} />
            </AppBarTitle>
          </AppBar>
          <ErrorState error={state.resource.error} />
        </div>
      );
  }

  if (state.resource.status === ResourceStatus.COMPLETE) {
      const selectedSpace = state.resource.data.space;
      // This hides sensors that are in an MPI
      // It needs a materialize_definition change to take effect
      const sensors = selectedSpace.installations.length > 0
        ? selectedSpace.installations.filter(sensor => !sensor.in_mpi)
        : [];
      const mpis = state.mpis.resource.status === ResourceStatus.COMPLETE && state.mpis.resource.data;

      return (
        <Fragment>
          <div className={styles.topBar}>
            <AppBar>
              <AppBarTitle>
                <BackButton />
                {selectedSpace.name}
              </AppBarTitle>
              <AppBarSection>
                <SensorActionButtons
                  presentation="list"
                  sensors={selectedSpace.installations.map(sensor => ({
                    serial_number: sensor.serial_number,
                    link_id: sensor.link_id,
                    organization: {
                      id: selectedSpace.organization_external_id,
                      name: selectedSpace.organization_name,
                    },
                    sensor_type: sensor.sensor_type,
                    os_version: sensor.os_version,
                    doorway_name: sensor.doorway_name,
                    diagnostic_info_json: sensor.diagnostic_info_json,
                    in_mpi: sensor.in_mpi,
                  }))}
                  primaryActionButton={
                    <Button
                      variant="filled"
                      href={`${
                        Hosts.CoreCucumberHost
                      }/api/space/${selectedSpace.external_id.replace(
                        /^spc_/,
                        '',
                      )}/change/`}
                    >
                      Edit in Core Cucumber
                    </Button>
                  }
                />
              </AppBarSection>
            </AppBar>
          </div>
          <div className={styles.scroll}>
            <div className={styles.datagrid}>
              <DataGrid
                items={[
                  {
                    id: 'organization',
                    label: 'Organization',
                    value: selectedSpace.organization_name,
                  },
                  {
                    id: 'capacity',
                    label: 'Capacity',
                    value: selectedSpace.capacity ? (
                      selectedSpace.capacity
                    ) : (
                      <Fragment>&mdash;</Fragment>
                    ),
                  },
                  {
                    id: 'spacetype',
                    label: 'Space Type',
                    value: selectedSpace.space_type,
                  },
                  {
                    id: 'function',
                    label: 'Space Function',
                    value: selectedSpace['function'] ? (
                      selectedSpace['function']
                    ) : (
                      <Fragment>&mdash;</Fragment>
                    ),
                  },
                  FORCE_BREAK,
                  {
                    id: 'maxdwell',
                    label: 'Max Dwell Time',
                    value:
                      selectedSpace.max_dwell_minutes
                      ? (
                        <span
                          style={{
                            color: colorVariables.red
                          }}
                        >
                          {`${selectedSpace.max_dwell_minutes} `} {selectedSpace.max_dwell_minutes > 1 ? 'minutes' : 'minute'}
                        </span>
                        )
                      : (
                        <span
                          style={{
                            color: colorVariables.green
                          }}
                        >
                          Not Set
                        </span>
                        )
                  },
                  {
                    id: 'resettime',
                    label: 'Daily Reset Time',
                    value: selectedSpace.daily_reset,
                  },
                  {
                    id: 'timezone',
                    label: 'Time Zone',
                    value: selectedSpace.time_zone,
                  },
                ]}
              />
            </div>

            <div className={classnames(styles.detailCard)}>
              <AppBar>
                <AppBarTitle>Sensors</AppBarTitle>
              </AppBar>
              {sensors.length > 0 ? (
                <ListView
                  padOuterColumns
                  headerHeight={48}
                  data={sensors}
                  onClickRow={item => {
                    openInNewTab(`#/${organizationIdOrAll}/sensors/${item.id}`);
                  }}
                >
                  <ListViewColumn
                    id="ID"
                    width={200}
                    template={item => item.id}
                  />
                  <ListViewColumn
                    id="Doorway Name"
                    template={item => item.doorway_name}
                  />
                  <ListViewColumnSpacer />
                  <ListViewColumn
                    id="Total Captures"
                    width={150}
                    template={item => item.total_captures}
                  />
                  {!ON_PREM ?
                    <ListViewColumn
                      id="Event Accuracy"
                      title={
                        <ListViewTwoRowHeader
                          header="Event Accuracy"
                          subheader="From the latest report"
                        />
                      }
                      width={200}
                      template={item => (
                        <ColoredPercent percent={item.latest_report ? item.latest_report.total_accuracy : null} />
                      )}
                    />:null}
                  <ListViewColumn
                    id=" "
                    width={100}
                    template={item => (
                      <DetailsButton
                        href={`#/${organizationIdOrAll}/sensors/${item.id}`}
                      />
                    )}
                  />
                </ListView>
              ) : (
                <div className={styles.noSensors}>
                  <p>No sensors found within this space.</p>
                </div>
              )}
            </div>

          {/* list mpis associated with this space */}

            <div className={classnames(styles.detailCard)}>
              <AppBar>
                <AppBarTitle>Multipoint Installations</AppBarTitle>
              </AppBar>
              {state.mpis.resource.status === ResourceStatus.ERROR &&
                <div className={styles.noSensors}>
                  <p>Error loading MPIs.</p>
                </div>
              }
              {(state.mpis.resource.status === ResourceStatus.COMPLETE && mpis.length > 0) ? (
                <ListView
                  padOuterColumns
                  headerHeight={48}
                  data={mpis}
                  onClickRow={item => {
                    openInNewTab(
                      `#/${organizationIdOrAll}/multi-point-installations/${item.id}/captures`,
                    );
                  }}
                >
                  <ListViewColumn
                    id="MPI Name"
                    template={item => item.name}
                  />
                  <ListViewColumnSpacer />
                  <ListViewColumn
                    id="Number of Sensors"
                    width={150}
                    template={item => item.number_of_sensors}
                  />
                  <ListViewColumnSpacer />
                  <ListViewColumn
                    id="Fully Ann. Cap. Groups"
                    width={200}
                    template={item => item.fully_annotated_capture_group_count}
                  />
                  {!ON_PREM ?
                    <ListViewColumn
                      id="Total Dedupe Accuracy"
                      title={
                        <ListViewTwoRowHeader
                          header="Total Accuracy"
                          subheader="From the latest report"
                        />
                      }
                      width={200}
                      template={item =>
                        item.fully_annotated_capture_group_count ? (
                          <ColoredPercent percent={item.total_dedupe_accuracy} />
                        ) : (
                          <Fragment>&mdash;</Fragment>
                        )
                      }
                    />:null}
                  <ListViewColumn
                    id=" "
                    width={100}
                    template={item => (
                      <DetailsButton
                        href={`#/${organizationIdOrAll}/multi-point-installations/${item.id}/captures`}
                      />
                    )}
                  />
                </ListView>
              ) : (
                <div className={styles.noSensors}>
                  <p>No MPIs found within this space.</p>
                </div>
              )}
            </div>

            <div
              className={classnames(styles.detailCard)}
              style={{ height: 618 }}
            >
              <CountsChart
                timeZone={state.resource.data.space.time_zone}
                date={state.counts.date}
                onChangeDate={date =>
                  dispatch({
                    type: SpacesActionTypes.SPACES_DETAIL_COUNTS_CHANGE_DATE,
                    date,
                  })
                }
                countsResource={state.counts.resource}
              />
            </div>
          </div>
        </Fragment>
      );
  }

  return (
      <div className={styles.wrapper}>
        <AppBar>
          <AppBarTitle>
            <BackButton />
            <Skeleton width={200} height={24} />
          </AppBarTitle>
        </AppBar>
        <Loading />
      </div>
    );
};

export default SpaceDetail;
